const MB = 1024 * 1024;
const PREVIEW_MAX_SIDE_PX = 640;

export interface ImageCompressionLimits {
  maxSizeMb?: number;
  maxPixels?: number;
  maxDimensionPx?: number;
  safetyRatio?: number;
}

export interface ImageMetadata {
  width: number;
  height: number;
  fileSize: number;
  mimeType: string;
}

export interface ImageCompressionTarget {
  width: number;
  height: number;
  scaleRatio: number;
  needsResize: boolean;
}

export interface ImageCompressionDecision {
  metadata: ImageMetadata;
  target: ImageCompressionTarget;
  needsCompression: boolean;
  reasons: Array<'size' | 'pixels' | 'dimension'>;
}

export interface CompressImageOptions {
  limits: ImageCompressionLimits;
  outputMimeType?: 'image/jpeg' | 'image/png' | 'image/webp';
  initialQuality?: number;
  minQuality?: number;
  qualityStep?: number;
  targetWidth?: number;
  targetHeight?: number;
  fileName?: string;
}

export interface CompressImageResult {
  file: File;
  metadata: ImageMetadata;
  target: ImageCompressionTarget;
  outputMimeType: string;
  quality?: number;
  originalSize: number;
  compressedSize: number;
}

function clampSafetyRatio(value: number | undefined): number {
  if (!Number.isFinite(value) || value == null) return 0.95;
  return Math.min(1, Math.max(0.1, value));
}

function normalizePositiveNumber(value: number | undefined): number | undefined {
  return Number.isFinite(value) && value != null && value > 0 ? value : undefined;
}

export function maxSizeBytes(maxSizeMb: number | undefined): number | undefined {
  const normalized = normalizePositiveNumber(maxSizeMb);
  return normalized == null ? undefined : normalized * MB;
}

export function calculateImageCompressionTarget(params: {
  width: number;
  height: number;
  limits: ImageCompressionLimits;
}): ImageCompressionTarget {
  const { width, height, limits } = params;
  const maxPixels = normalizePositiveNumber(limits.maxPixels);
  const maxDimensionPx = normalizePositiveNumber(limits.maxDimensionPx);
  const safetyRatio = clampSafetyRatio(limits.safetyRatio);
  const ratios: number[] = [];

  if (maxPixels != null && width * height > maxPixels) {
    ratios.push(Math.sqrt(maxPixels / (width * height)));
  }

  const maxSide = Math.max(width, height);
  if (maxDimensionPx != null && maxSide > maxDimensionPx) {
    ratios.push(maxDimensionPx / maxSide);
  }

  const limitRatio = ratios.length ? Math.min(...ratios) : 1;
  const scaleRatio = limitRatio < 1 ? limitRatio * safetyRatio : 1;
  const targetWidth = Math.max(1, Math.floor(width * scaleRatio));
  const targetHeight = Math.max(1, Math.floor(height * scaleRatio));

  return {
    width: targetWidth,
    height: targetHeight,
    scaleRatio,
    needsResize: targetWidth !== width || targetHeight !== height,
  };
}

export async function readImageMetadata(file: Blob): Promise<ImageMetadata> {
  const mimeType = file.type || 'application/octet-stream';
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(file);
    try {
      return {
        width: bitmap.width,
        height: bitmap.height,
        fileSize: file.size,
        mimeType,
      };
    } finally {
      bitmap.close();
    }
  }

  const objectUrl = URL.createObjectURL(file);
  try {
    const dimensions = await new Promise<{ width: number; height: number }>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
      image.onerror = () => reject(new Error('无法读取图片尺寸'));
      image.src = objectUrl;
    });
    return { ...dimensions, fileSize: file.size, mimeType };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export function inspectImageCompression(
  metadata: ImageMetadata,
  limits: ImageCompressionLimits
): ImageCompressionDecision {
  const target = calculateImageCompressionTarget({
    width: metadata.width,
    height: metadata.height,
    limits,
  });
  const reasons: ImageCompressionDecision['reasons'] = [];
  const maxBytes = maxSizeBytes(limits.maxSizeMb);
  const maxPixels = normalizePositiveNumber(limits.maxPixels);
  const maxDimensionPx = normalizePositiveNumber(limits.maxDimensionPx);

  if (maxBytes != null && metadata.fileSize > maxBytes) reasons.push('size');
  if (maxPixels != null && metadata.width * metadata.height > maxPixels) reasons.push('pixels');
  if (maxDimensionPx != null && Math.max(metadata.width, metadata.height) > maxDimensionPx) {
    reasons.push('dimension');
  }

  return {
    metadata,
    target,
    needsCompression: reasons.length > 0 || target.needsResize,
    reasons,
  };
}

function chooseOutputMimeType(
  inputMimeType: string,
  outputMimeType?: CompressImageOptions['outputMimeType']
): 'image/jpeg' | 'image/png' | 'image/webp' {
  if (outputMimeType) return outputMimeType;
  if (inputMimeType === 'image/png') return 'image/png';
  if (inputMimeType === 'image/webp') return 'image/webp';
  return 'image/jpeg';
}

function extensionForMimeType(mimeType: string): string {
  if (mimeType === 'image/png') return 'png';
  if (mimeType === 'image/webp') return 'webp';
  return 'jpg';
}

function replaceFileExtension(fileName: string, mimeType: string): string {
  const extension = extensionForMimeType(mimeType);
  const baseName = fileName.replace(/\.[^.]+$/, '');
  return `${baseName || 'image'}.${extension}`;
}

async function loadImageBitmap(file: Blob): Promise<ImageBitmap> {
  if (typeof createImageBitmap !== 'function') {
    throw new Error('当前浏览器不支持图片压缩');
  }
  return createImageBitmap(file);
}

async function canvasToBlob(
  canvas: HTMLCanvasElement,
  mimeType: string,
  quality?: number
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('图片压缩失败'));
          return;
        }
        resolve(blob);
      },
      mimeType,
      quality
    );
  });
}

export async function createImagePreviewUrl(
  file: Blob,
  maxSide = PREVIEW_MAX_SIDE_PX
): Promise<string> {
  const bitmap = await loadImageBitmap(file);

  try {
    const longestSide = Math.max(bitmap.width, bitmap.height);
    const scale = longestSide > maxSide ? maxSide / longestSide : 1;
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('当前浏览器不支持图片预览');

    context.drawImage(bitmap, 0, 0, width, height);
    const blob = await canvasToBlob(canvas, 'image/jpeg', 0.85);
    return URL.createObjectURL(blob);
  } finally {
    bitmap.close();
  }
}

export async function compressImageFile(
  file: File,
  options: CompressImageOptions
): Promise<CompressImageResult> {
  const metadata = await readImageMetadata(file);
  const computedTarget = calculateImageCompressionTarget({
    width: metadata.width,
    height: metadata.height,
    limits: options.limits,
  });
  const targetWidth =
    Number.isFinite(options.targetWidth) && options.targetWidth != null && options.targetWidth > 0
      ? Math.max(1, Math.floor(options.targetWidth))
      : computedTarget.width;
  const targetHeight =
    Number.isFinite(options.targetHeight) &&
    options.targetHeight != null &&
    options.targetHeight > 0
      ? Math.max(1, Math.floor(options.targetHeight))
      : computedTarget.height;
  const target: ImageCompressionTarget = {
    width: targetWidth,
    height: targetHeight,
    scaleRatio: Math.min(targetWidth / metadata.width, targetHeight / metadata.height),
    needsResize: targetWidth !== metadata.width || targetHeight !== metadata.height,
  };
  const outputMimeType = chooseOutputMimeType(metadata.mimeType, options.outputMimeType);
  const maxBytes = maxSizeBytes(options.limits.maxSizeMb);
  const initialQuality = Math.min(1, Math.max(0.1, options.initialQuality ?? 0.92));
  const minQuality = Math.min(initialQuality, Math.max(0.1, options.minQuality ?? 0.6));
  const qualityStep = Math.min(0.3, Math.max(0.01, options.qualityStep ?? 0.08));
  const bitmap = await loadImageBitmap(file);

  try {
    const canvas = document.createElement('canvas');
    canvas.width = target.width;
    canvas.height = target.height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('当前浏览器不支持 Canvas 图片压缩');
    context.drawImage(bitmap, 0, 0, target.width, target.height);

    let quality: number | undefined = outputMimeType === 'image/png' ? undefined : initialQuality;
    let blob = await canvasToBlob(canvas, outputMimeType, quality);

    while (
      maxBytes != null &&
      blob.size > maxBytes &&
      quality != null &&
      quality - qualityStep >= minQuality
    ) {
      quality = Math.max(minQuality, quality - qualityStep);
      blob = await canvasToBlob(canvas, outputMimeType, quality);
    }

    if (maxBytes != null && blob.size > maxBytes) {
      throw new Error(`压缩后图片仍超过 ${options.limits.maxSizeMb}MB 限制`);
    }

    const compressedFile = new File(
      [blob],
      replaceFileExtension(options.fileName ?? file.name, outputMimeType),
      {
        type: outputMimeType,
        lastModified: Date.now(),
      }
    );

    return {
      file: compressedFile,
      metadata,
      target,
      outputMimeType,
      quality,
      originalSize: file.size,
      compressedSize: compressedFile.size,
    };
  } finally {
    bitmap.close();
  }
}
